'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@achra/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@achra/ui/dropdown-menu';
import { cn } from '@achra/ui/lib/utils';
import { LinkIcon } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import DiscordSVG from '@/modules/shared/components/svgs/discord.svg';
import ForumSVG from '@/modules/shared/components/svgs/forum.svg';
import GithubSVG from '@/modules/shared/components/svgs/github.svg';
import WebsiteSVG from '@/modules/shared/components/svgs/website.svg';
import TwitterSVG from '@/modules/shared/components/svgs/x.svg';
import YoutubeSVG from '@/modules/shared/components/svgs/youtube.svg';
import { isSocialMediaType } from '@/modules/shared/lib/is-social-media-type';
import type { Route } from 'next';
type SocialMedia = 'website' | 'forum' | 'discord' | 'x' | 'github' | 'youtube';
const MEDIA_ICON_MAP: Record<SocialMedia, React.ReactNode> = {
  website: <WebsiteSVG className="size-4" />,
  forum: <ForumSVG className="size-4" />,
  discord: <DiscordSVG className="size-4" />,
  x: <TwitterSVG className="size-4" />,
  github: <GithubSVG className="size-4" />,
  youtube: <YoutubeSVG className="size-4" />
};
const MEDIA_LABEL_MAP: Record<SocialMedia, string> = {
  website: 'Website',
  forum: 'Forum',
  discord: 'Discord',
  x: 'Twitter',
  github: 'Github',
  youtube: 'Youtube'
};
interface MediaElement {
  type: string;
  href: string;
}
interface LinkElement {
  href: string;
  label: string;
  icon: React.ReactNode;
  isExternal?: boolean;
}
interface LinksPopoverProps {
  links: Array<MediaElement | LinkElement>;
  children?: React.ReactNode;
  renderLinkItem?: (link: MediaElement | LinkElement) => React.ReactElement;
  defaultOpen?: boolean;
}
function defaultRenderLinkItem(link: MediaElement | LinkElement) {
  return <LinksPopoverItem key={`${link.href}-${'type' in link ? link.type : link.label}`} link={link} />;
}

/**
 * A dropdown menu component that displays a list of links (social media or custom links).
 *
 * @example Basic Usage with Social Media Links
 * ```tsx
 * <LinksPopover links={[
 *     { type: 'website', href: 'https://example.com' },
 *     { type: 'twitter', href: 'https://twitter.com/example' },
 *     { type: 'github', href: 'https://github.com/example' },
 *   ]}
 * >
 *   <Button variant="secondary">Links</Button>
 * </LinksPopover>
 * ```
 *
 * @param links - Array of links to display in the popover
 * @param children - Trigger element that opens the popover on hover
 * @param renderLinkItem - Function to render a link item
 * @param defaultOpen - Whether the popover should be open by default (useful for testing purposes)
 */
function LinksPopover(t0) {
  const $ = _c(25);
  const {
    links,
    children,
    renderLinkItem: t1,
    defaultOpen: t2
  } = t0;
  const renderLinkItem = t1 === undefined ? defaultRenderLinkItem : t1;
  const defaultOpen = t2 === undefined ? false : t2;
  const [open, setOpen] = useState(defaultOpen);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setOpen(false);
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const handleMouseLeave = useDebounceCallback(t3, 100);
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = newState => {
      setOpen(newState);
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== handleMouseLeave) {
    t5 = () => {
      handleMouseLeave.cancel();
      setOpen(true);
    };
    $[2] = handleMouseLeave;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== children) {
    t6 = children ?? <Button variant="secondary"><LinkIcon /> <span className="sm:hidden md:inline">Links</span></Button>;
    $[4] = children;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== handleMouseLeave || $[7] !== t5 || $[8] !== t6) {
    t7 = <DropdownMenuTrigger onMouseEnter={t5} onMouseLeave={handleMouseLeave} asChild={true}>{t6}</DropdownMenuTrigger>;
    $[6] = handleMouseLeave;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== handleMouseLeave) {
    t8 = () => {
      handleMouseLeave.cancel();
    };
    $[10] = handleMouseLeave;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== links || $[13] !== renderLinkItem) {
    let t10;
    if ($[15] !== renderLinkItem) {
      t10 = link => renderLinkItem(link);
      $[15] = renderLinkItem;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    t9 = links.map(t10);
    $[12] = links;
    $[13] = renderLinkItem;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[17] !== handleMouseLeave || $[18] !== t8 || $[19] !== t9) {
    t10 = <DropdownMenuContent onMouseEnter={t8} onMouseLeave={handleMouseLeave} align="end">{t9}</DropdownMenuContent>;
    $[17] = handleMouseLeave;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== open || $[22] !== t10 || $[23] !== t7) {
    t11 = <DropdownMenu modal={false} open={open} onOpenChange={t4}>{t7}{t10}</DropdownMenu>;
    $[21] = open;
    $[22] = t10;
    $[23] = t7;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}
function LinkContent(t0) {
  const $ = _c(9);
  const {
    link
  } = t0;
  if ("type" in link) {
    const linkType = link.type.toLowerCase();
    if (isSocialMediaType(linkType)) {
      const t1 = MEDIA_ICON_MAP[linkType];
      const t2 = MEDIA_LABEL_MAP[linkType];
      let t3;
      if ($[0] !== t1 || $[1] !== t2) {
        t3 = <>{t1}{t2}</>;
        $[0] = t1;
        $[1] = t2;
        $[2] = t3;
      } else {
        t3 = $[2];
      }
      return t3;
    }
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <WebsiteSVG className="size-4" />;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== link.type) {
      t2 = <>{t1}{link.type}</>;
      $[4] = link.type;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t1;
  if ($[6] !== link.icon || $[7] !== link.label) {
    t1 = <>{link.icon}{link.label}</>;
    $[6] = link.icon;
    $[7] = link.label;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
function getLinkExternalProps(link: MediaElement | LinkElement) {
  const isExternal = 'type' in link ? true : Boolean(link.isExternal);
  return isExternal ? {
    target: '_blank' as const,
    rel: 'noopener noreferrer'
  } : {};
}
interface LinksPopoverItemProps extends React.ComponentProps<typeof DropdownMenuItem> {
  link: MediaElement | LinkElement;
}
function LinksPopoverItem(t0) {
  const $ = _c(15);
  let link;
  let props;
  if ($[0] !== t0) {
    ({
      link,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = link;
    $[2] = props;
  } else {
    link = $[1];
    props = $[2];
  }
  const t1 = link.href;
  const t2 = link.href as Route;
  let t3;
  if ($[3] !== link) {
    t3 = getLinkExternalProps(link);
    $[3] = link;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== link) {
    t4 = <LinkContent link={link} />;
    $[5] = link;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <Link href={t2} {...t3}>{t4}</Link>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== link.href || $[12] !== props || $[13] !== t5) {
    t6 = <DropdownMenuItem key={t1} asChild={true} className="hover:cursor-pointer" {...props}>{t5}</DropdownMenuItem>;
    $[11] = link.href;
    $[12] = props;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
interface LinksListProps {
  links: Array<MediaElement | LinkElement>;
  className?: string;
}

/**
 * Renders links as a flat, always-visible list of anchors (no popover/trigger),
 * reusing the same social-media icons and labels as {@link LinksPopover}.
 */
function LinksList(t0) {
  const $ = _c(7);
  const {
    links,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-col gap-1", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== links) {
    t2 = links.map(_temp);
    $[2] = links;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <ul className={t1}>{t2}</ul>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function _temp(link) {
  return <li key={`${link.href}-${"type" in link ? link.type : link.label}`}><Link href={link.href as Route} {...getLinkExternalProps(link)} className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-2 text-sm [&_svg]:size-4"><LinkContent link={link} /></Link></li>;
}
export { LinksPopover, LinksPopoverItem, LinksList, type SocialMedia, type MediaElement, type LinkElement };